import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { getShortUrl, abTestApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import {
  Copy,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  PencilSimple,
  Trash,
  DownloadSimple,
  CursorClick,
  Users,
  QrCode,
  Clock,
  Lightbulb,
  Flask,
  Trophy,
  Plus,
  X,
  Signpost,
  Globe,
  ShieldCheck,
  WifiHigh,
  Sparkle,
  Buildings,
  MapPin,
  Eye,
  ArrowClockwise,
  CheckCircle,
  Warning,
  MinusCircle,
  Bell,
  Heart,
  CircleNotch,
} from '@phosphor-icons/react';
import LiveClickFeed from '../components/LiveClickFeed';
import WorldClickMap from '../components/WorldClickMap';
import AlertRulesPanel from '../components/AlertRulesPanel';
import { HealthBadgeLarge } from '../components/HealthBadge';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import '../styles/pages.css';
import './LinkDetailPage.css';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import {
  ResponsiveContainer,
  ComposedChart,
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const PALETTE = ['#4648d4', '#6063ee', '#8B87FF', '#3CC68C', '#F5A524', '#F26C6C'];

const TooltipChrome = {
  contentStyle: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 12,
    border: '1px solid hsl(var(--border))',
    borderRadius: 12,
    boxShadow: '0 12px 30px -12px rgba(15, 16, 32, 0.18)',
    background: '#fff',
    padding: '10px 12px',
  },
  labelStyle: { color: 'hsl(var(--muted-foreground))', fontWeight: 500 },
};

const DONUT_COLORS_MOCK = ['#ff5436', '#8b9b3f', '#e8b94a', '#a8a8af', '#4648d4'];

const MOCK_COUNTRY_FLAG = {
  'United States': 'us',
  USA: 'us',
  Netherlands: 'nl',
  Germany: 'de',
  France: 'fr',
  'United Kingdom': 'gb',
  UK: 'gb',
  Japan: 'jp',
  India: 'in',
  Canada: 'ca',
  Australia: 'au',
  Brazil: 'br',
  Spain: 'es',
  Italy: 'it',
  Mexico: 'mx',
  Singapore: 'sg',
  Sweden: 'se',
  Norway: 'no',
  Poland: 'pl',
};

const COUNTRY_PRIMARY_LANG = {
  'United States': { code: 'EN', name: 'English' },
  'United Kingdom': { code: 'EN', name: 'English' },
  Canada: { code: 'EN', name: 'English' },
  Australia: { code: 'EN', name: 'English' },
  Netherlands: { code: 'NL', name: 'Dutch' },
  Germany: { code: 'DE', name: 'German' },
  France: { code: 'FR', name: 'French' },
  Spain: { code: 'ES', name: 'Spanish' },
  Italy: { code: 'IT', name: 'Italian' },
  Japan: { code: 'JA', name: 'Japanese' },
  Brazil: { code: 'PT', name: 'Portuguese' },
  India: { code: 'EN', name: 'English' },
  Mexico: { code: 'ES', name: 'Spanish' },
  Singapore: { code: 'EN', name: 'English' },
  Sweden: { code: 'SV', name: 'Swedish' },
  Norway: { code: 'NO', name: 'Norwegian' },
  Poland: { code: 'PL', name: 'Polish' },
};

const ISO_A2_TO_COUNTRY_NAME = {
  US: 'United States',
  GB: 'United Kingdom',
  DE: 'Germany',
  NL: 'Netherlands',
  FR: 'France',
  AU: 'Australia',
  CA: 'Canada',
  JP: 'Japan',
  IN: 'India',
  BR: 'Brazil',
  ES: 'Spain',
  IT: 'Italy',
  PL: 'Poland',
  SG: 'Singapore',
  SE: 'Sweden',
  NO: 'Norway',
  DK: 'Denmark',
  FI: 'Finland',
};

function resolveCountryDisplayName(raw) {
  if (!raw) return '';
  const s = String(raw).trim();
  if (/^[A-Z]{2}$/i.test(s)) return ISO_A2_TO_COUNTRY_NAME[s.toUpperCase()] || s.toUpperCase();
  return s;
}

function flagClassForCountry(countryName) {
  if (!countryName) return null;
  const t = String(countryName).trim();
  const hit = Object.keys(MOCK_COUNTRY_FLAG).find((k) => k.toLowerCase() === t.toLowerCase());
  return hit ? MOCK_COUNTRY_FLAG[hit] : null;
}

function CountryFlagMark({ countryName }) {
  const code = flagClassForCountry(countryName);
  if (code === 'nl') {
    return (
      <span className="lfld-flag lfld-flag-nl" aria-hidden style={{ width: 18, height: 13 }}>
        <span />
        <span />
        <span />
      </span>
    );
  }
  if (!code) return null;
  return <span className={`lfld-flag lfld-flag-${code}`} aria-hidden style={{ width: 18, height: 13 }} />;
}

function inferLanguagesFromCountries(countries) {
  const tally = {};
  for (const c of countries || []) {
    const resolved = resolveCountryDisplayName(c.name) || c.name;
    const L = COUNTRY_PRIMARY_LANG[resolved] || COUNTRY_PRIMARY_LANG[c.name] || { code: '•', name: 'Other' };
    const key = `${L.code}|${L.name}`;
    tally[key] = (tally[key] || 0) + (c.value || 0);
  }
  const total = Object.values(tally).reduce((s, n) => s + n, 0);
  return Object.entries(tally)
    .map(([key, value]) => {
      const [code, name] = key.split('|');
      return { code, name, value, pct: total > 0 ? Math.round((value / total) * 100) : 0 };
    })
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);
}

function refererHostname(raw) {
  if (!raw || raw === 'direct') return '(direct)';
  try {
    const u = String(raw).includes('://') ? new URL(raw) : new URL(`https://${raw}`);
    return u.hostname.replace(/^www\./, '') || String(raw);
  } catch {
    return String(raw).slice(0, 48);
  }
}

function barFillVariant(i) {
  if (i === 0) return '';
  if (i === 1) return 'lfld-bar-fill-olive';
  if (i === 2) return 'lfld-bar-fill-mustard';
  return 'lfld-bar-fill-dim';
}

function historyTimelineDotClass(i) {
  const m = i % 3;
  if (m === 1) return 'lfld-tl-dot-olive';
  if (m === 2) return 'lfld-tl-dot-mustard';
  return '';
}

function buildUtmQuery(utm) {
  const p = new URLSearchParams();
  if (utm.source) p.set('utm_source', utm.source);
  if (utm.medium) p.set('utm_medium', utm.medium);
  if (utm.campaign) p.set('utm_campaign', utm.campaign);
  if (utm.term) p.set('utm_term', utm.term);
  if (utm.content) p.set('utm_content', utm.content);
  return p.toString();
}

function utmPairsFromBuilder(utm) {
  const pairs = [];
  if (utm.source) pairs.push(['utm_source', utm.source]);
  if (utm.medium) pairs.push(['utm_medium', utm.medium]);
  if (utm.campaign) pairs.push(['utm_campaign', utm.campaign]);
  if (utm.term) pairs.push(['utm_term', utm.term]);
  if (utm.content) pairs.push(['utm_content', utm.content]);
  return pairs;
}

const ROUTING_OS_OPTIONS = ['Windows', 'macOS', 'Linux', 'Android', 'iOS', 'Other'];

function parseRoutingDeviceOs(raw) {
  try {
    const o = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return {
      device: ['mobile', 'tablet', 'desktop'].includes(o?.device) ? o.device : 'mobile',
      os: ROUTING_OS_OPTIONS.includes(o?.os) ? o.os : 'iOS',
    };
  } catch {
    return { device: 'mobile', os: 'iOS' };
  }
}

function destinationDisplayHost(urlStr) {
  if (!urlStr) return '—';
  try {
    const u = urlStr.includes('://') ? new URL(urlStr) : new URL(`https://${urlStr}`);
    return u.hostname.replace(/^www\./, '') || urlStr.slice(0, 32);
  } catch {
    return String(urlStr).slice(0, 40);
  }
}

function shortenUrlDisplay(s, max = 42) {
  if (!s) return '';
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

function classifyHistoryEntry(entry) {
  const pairs = Object.entries(entry?.changes || {});
  const labels = pairs.map(([k]) => String(k).toLowerCase()).join(' ');
  const blob = `${labels} ${JSON.stringify(entry?.changes)}`.toLowerCase();
  if (blob.includes('utm') || labels.includes('utm')) return { label: 'UTM', cls: 'bg-amber-100 text-amber-800' };
  if (blob.includes('qr') || blob.includes('download')) return { label: 'QR', cls: 'bg-violet-100 text-violet-800' };
  if (blob.includes('alert')) return { label: 'Alert', cls: 'bg-rose-100 text-rose-800' };
  if (blob.includes('destination') || blob.includes('slug') || blob.includes('title') || blob.includes('status'))
    return { label: 'Edit', cls: 'bg-sky-100 text-sky-800' };
  return { label: 'Change', cls: 'bg-muted text-muted-foreground' };
}

function matchesHistoryFilter(entry, filter) {
  if (!filter || filter === 'all') return true;
  const { label } = classifyHistoryEntry(entry);
  if (filter === 'misc') return label === 'Change';
  return label.toLowerCase() === filter;
}

function readUtmTemplates(workspaceId) {
  if (!workspaceId || typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(`linkflow_utm_templates:${workspaceId}`);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function writeUtmTemplates(workspaceId, list) {
  if (!workspaceId || typeof window === 'undefined') return;
  window.localStorage.setItem(`linkflow_utm_templates:${workspaceId}`, JSON.stringify(list));
}

/** Heuristic tab jump from AI recommendation copy */
function insightRecNextTab(rec) {
  const t = String(rec).toLowerCase();
  if (t.includes('utm') || t.includes('campaign')) return 'utm';
  if (t.includes('audienc') || t.includes('country') || t.includes('geo') || t.includes('region')) return 'audience';
  if (t.includes('device') || t.includes('mobile') || t.includes('tablet')) return 'devices';
  if (t.includes('referr') || t.includes('source') || t.includes('channel')) return 'referrers';
  return 'overview';
}

function ReferrerFaviconIcon({ rawName }) {
  const host = refererHostname(rawName);
  const [failed, setFailed] = useState(false);
  const d =
    !host || host === '(direct)'
      ? null
      : host.replace(/^www\./, '').split('/')[0];
  if (!d) {
    return <span className="lfld-bar-icon lfld-bar-icon-muted" aria-hidden>—</span>;
  }
  if (failed) {
    return (
      <span className="lfld-bar-icon" aria-hidden>
        {d.slice(0, 2).toUpperCase()}
      </span>
    );
  }
  return (
    <span className="lfld-bar-icon">
      <img
        src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(d)}&sz=32`}
        alt=""
        width={18}
        height={18}
        style={{ display: 'block' }}
        onError={() => setFailed(true)}
      />
    </span>
  );
}

function LfldPieDonutBlock({ rows, centerLine1, centerLine2, valueLabel = 'clicks' }) {
  const filtered = (rows || []).filter((d) => d.value > 0);
  const total = filtered.reduce((s, d) => s + d.value, 0);
  if (total === 0) {
    return <p className="text-sm px-[22px] pb-[22px]" style={{ color: 'var(--ink-dim)' }}>No data</p>;
  }
  const fmtMiddle = typeof centerLine1 === 'number' ? centerLine1.toLocaleString() : centerLine1;
  return (
    <div className="lfld-donut-wrap lfld-donut-wrap-column">
      <div className="lfld-donut-chart-slot">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={filtered}
              dataKey="value"
              innerRadius={52}
              outerRadius={70}
              paddingAngle={1}
              stroke="none"
            >
              {filtered.map((_, i) => (
                <Cell key={i} fill={DONUT_COLORS_MOCK[i % DONUT_COLORS_MOCK.length]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="lfld-donut-center">
          {centerLine1 != null && centerLine1 !== '' && (
            <div className="lfld-donut-center-num">{fmtMiddle}</div>
          )}
          {centerLine2 && <div className="lfld-donut-center-sub">{centerLine2}</div>}
        </div>
      </div>
      <div className="lfld-donut-legend lfld-donut-legend-full">
        {filtered.map((d, i) => {
          const pct = Math.round((d.value / total) * 100);
          return (
            <div key={d.name} className="lfld-donut-row">
              <div className="lfld-donut-label">
                <span
                  className="lfld-donut-swatch"
                  style={{ background: DONUT_COLORS_MOCK[i % DONUT_COLORS_MOCK.length] }}
                />
                {d.name}
              </div>
              <div className="lfld-donut-meta">
                <div className="lfld-donut-pct">{pct}%</div>
                <div className="lfld-donut-num-small">
                  {d.value.toLocaleString()} {valueLabel}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function LinkDetailPage() {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const { workspace } = useAuth();
  const [link, setLink] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [dateRange, setDateRange] = useState('30d');
  const [granularity, setGranularity] = useState('day');
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editPixels, setEditPixels] = useState([]);
  const [availablePixels, setAvailablePixels] = useState([]);
  const [availableCtas, setAvailableCtas] = useState([]);
  const [availableFolders, setAvailableFolders] = useState([]);
  const [showQR, setShowQR] = useState(false);
  const [qrFg, setQrFg] = useState('#4648d4');
  const [qrBg, setQrBg] = useState('#FFFFFF');
  const [scoreData, setScoreData] = useState(null);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [includeBots, setIncludeBots] = useState(false);

  const [abStats, setAbStats] = useState(null);
  const [abVariants, setAbVariants] = useState([
    { url: '', weight: 50 },
    { url: '', weight: 50 },
  ]);
  const [abSaving, setAbSaving] = useState(false);

  const [aiInsights, setAiInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsRange, setInsightsRange] = useState('30d');

  const [routingRules, setRoutingRules] = useState([]);
  const [routingSaving, setRoutingSaving] = useState(false);

  // Health monitoring state
  const [healthData, setHealthData] = useState(null);
  const [healthHistory, setHealthHistory] = useState([]);
  const [healthChecking, setHealthChecking] = useState(false);

  const [utmBuilder, setUtmBuilder] = useState({
    source: '',
    medium: '',
    campaign: '',
    term: '',
    content: '',
  });

  const [detailTab, setDetailTab] = useState('overview');
  const [historyFilter, setHistoryFilter] = useState('all');
  const [utmSaveTemplateOpen, setUtmSaveTemplateOpen] = useState(false);
  const [utmTemplateName, setUtmTemplateName] = useState('');
  const [utmTemplateTick, setUtmTemplateTick] = useState(0);

  const utmSavedTemplates = useMemo(
    () => readUtmTemplates(workspace?.workspace_id),
    [workspace?.workspace_id, utmTemplateTick]
  );

  const healthLatencySparkline = useMemo(() => {
    const pts = [...(healthHistory || [])]
      .slice(0, 12)
      .reverse()
      .map((h) => Number(h.response_time_ms))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (pts.length < 2) return null;
    const max = Math.max(...pts);
    const w = 100;
    const h = 28;
    const coords = pts
      .map((v, i) => {
        const x = (i / Math.max(pts.length - 1, 1)) * w;
        const y = h - (max > 0 ? (v / max) * (h - 4) : 0) - 2;
        return `${x},${y}`;
      })
      .join(' ');
    return coords;
  }, [healthHistory]);

  const avgHealthLatencyMs =
    healthHistory?.length > 0
      ? Math.round(
          healthHistory.reduce((s, h) => s + (Number(h.response_time_ms) || 0), 0) / healthHistory.length
        )
      : null;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [linkRes, analyticsRes] = await Promise.all([
          api.get(`/links/${linkId}`),
          api.get(`/analytics/link/${linkId}`, { params: { date_range: dateRange, granularity, include_bots: includeBots } }),
        ]);
        setLink(linkRes.data);
        setAnalytics(analyticsRes.data);
        setEditForm({
          destination_url: linkRes.data.destination_url,
          title: linkRes.data.title || '',
          tags: linkRes.data.tags?.join(', ') || '',
          utm_source: linkRes.data.utm_params?.source || '',
          utm_medium: linkRes.data.utm_params?.medium || '',
          utm_campaign: linkRes.data.utm_params?.campaign || '',
          utm_term: linkRes.data.utm_params?.term || '',
          utm_content: linkRes.data.utm_params?.content || '',
          expiry_date: linkRes.data.expiry_date ? linkRes.data.expiry_date.slice(0, 16) : '',
          max_clicks: linkRes.data.max_clicks != null ? String(linkRes.data.max_clicks) : '',
          password: linkRes.data.password || '',
          cta_overlay_id: linkRes.data.cta_overlay_id || '',
          status: linkRes.data.status || 'active',
          folder_id: linkRes.data.folder_id || 'none',
        });
        setEditPixels(linkRes.data.retargeting_pixels || []);
        setRoutingRules(
          (linkRes.data.routing_rules || []).map((r) => ({
            condition_type: r.condition_type || '',
            condition_value: r.condition_value || '',
            destination_url: r.destination_url || '',
          }))
        );
      } catch {
        toast.error('Failed to load link');
        navigate('/links');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [linkId, dateRange, granularity, includeBots, navigate]);

  useEffect(() => {
    if (!link?.link_id) return;
    setUtmBuilder({
      source: link.utm_params?.source || '',
      medium: link.utm_params?.medium || '',
      campaign: link.utm_params?.campaign || '',
      term: link.utm_params?.term || '',
      content: link.utm_params?.content || '',
    });
  }, [link?.link_id, link?.utm_params]);

  useEffect(() => {
    setScoreData(null);
    setScoreLoading(false);
  }, [linkId]);

  useEffect(() => {
    const id = link?.link_id;
    if (!id || id !== linkId) return;
    let cancelled = false;
    setScoreLoading(true);
    api
      .post('/ai/link-score', { link_id: id })
      .then((res) => {
        if (!cancelled) setScoreData(res.data || null);
      })
      .catch(() => {
        if (!cancelled) setScoreData(null);
      })
      .finally(() => {
        if (!cancelled) setScoreLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [link?.link_id]);

  const fetchInsights = useCallback(async (range = insightsRange) => {
    if (!link?.link_id) return;
    setInsightsLoading(true);
    try {
      const res = await api.post('/ai/click-insights', { link_id: link.link_id, date_range: range });
      setAiInsights(res.data);
    } catch {
      toast.error('Could not load AI insights');
    } finally {
      setInsightsLoading(false);
    }
  }, [link?.link_id, insightsRange]);

  const fetchHealth = useCallback(async () => {
    if (!linkId) return;
    try {
      const [statusRes, historyRes] = await Promise.all([
        api.get(`/links/${linkId}/health`).catch(() => null),
        api.get(`/links/${linkId}/health/history`).catch(() => ({ data: [] })),
      ]);
      if (statusRes?.data) setHealthData(statusRes.data);
      setHealthHistory(historyRes.data || []);
    } catch {
      // Health data not critical
    }
  }, [linkId]);

  const handleCheckNow = async () => {
    setHealthChecking(true);
    try {
      const res = await api.get(`/links/${linkId}/health`);
      setHealthData(res.data);
      toast.success('Health check complete');
      fetchHealth();
    } catch {
      toast.error('Health check failed');
    } finally {
      setHealthChecking(false);
    }
  };

  const fetchAbStats = useCallback(async () => {
    if (!link?.link_id) return;
    try {
      const res = await abTestApi.getStats(link.link_id);
      setAbStats(res.data);
      if (res.data?.variants?.length > 0) {
        setAbVariants(res.data.variants.map((v) => ({ url: v.url, weight: v.weight })));
      }
    } catch {
      setAbStats(null);
    }
  }, [link?.link_id]);

  useEffect(() => {
    fetchAbStats();
  }, [fetchAbStats]);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  useEffect(() => {
    if (showEdit && workspace?.workspace_id) {
      api
        .get(`/workspaces/${workspace.workspace_id}/pixels`)
        .then((res) => setAvailablePixels(res.data))
        .catch(() => {});
      api
        .get(`/workspaces/${workspace.workspace_id}/ctas`)
        .then((res) => setAvailableCtas(res.data))
        .catch(() => {});
      api
        .get(`/workspaces/${workspace.workspace_id}/folders`)
        .then((res) => setAvailableFolders(res.data || []))
        .catch(() => setAvailableFolders([]));
    }
  }, [showEdit, workspace?.workspace_id]);

  const handleCopy = async () => {
    const text = getShortUrl(link);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    toast.success('Short URL copied');
  };

  const handleSaveEdit = async () => {
    try {
      const payload = {
        destination_url: editForm.destination_url,
        title: editForm.title,
        tags: editForm.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        status: editForm.status,
      };
      const utm = {};
      if (editForm.utm_source) utm.source = editForm.utm_source;
      if (editForm.utm_medium) utm.medium = editForm.utm_medium;
      if (editForm.utm_campaign) utm.campaign = editForm.utm_campaign;
      if (editForm.utm_term) utm.term = editForm.utm_term;
      if (editForm.utm_content) utm.content = editForm.utm_content;
      if (Object.keys(utm).length > 0) payload.utm_params = utm;

      payload.expiry_date = editForm.expiry_date ? new Date(editForm.expiry_date).toISOString() : null;
      payload.max_clicks = editForm.max_clicks ? parseInt(editForm.max_clicks) : null;
      payload.password = editForm.password ? editForm.password : null;
      payload.cta_overlay_id =
        editForm.cta_overlay_id && editForm.cta_overlay_id !== 'none' ? editForm.cta_overlay_id : null;
      payload.retargeting_pixels = editPixels;
      payload.folder_id =
        editForm.folder_id && editForm.folder_id !== 'none' ? editForm.folder_id : null;

      const res = await api.put(`/links/${linkId}`, payload);
      setLink(res.data);
      setShowEdit(false);
      toast.success('Link updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this link?')) return;
    try {
      await api.delete(`/links/${linkId}`);
      toast.success('Link deleted');
      navigate('/links');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleDownloadQR = () => {
    const svg = document.querySelector('#qr-code-svg svg');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `linkflow-${link.slug}-qr.svg`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('QR code downloaded');
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`/links/${linkId}/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `linkflow-${link?.slug}-clicks.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Analytics exported');
    } catch {
      toast.error('Failed to export');
    }
  };

  const handleSaveUtmTemplate = () => {
    const name = utmTemplateName.trim();
    if (!name) {
      toast.error('Name your template');
      return;
    }
    if (!workspace?.workspace_id) {
      toast.error('No workspace');
      return;
    }
    const id = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `utm_${Date.now()}`;
    const row = {
      id,
      name,
      source: utmBuilder.source || '',
      medium: utmBuilder.medium || '',
      campaign: utmBuilder.campaign || '',
      term: utmBuilder.term || '',
      content: utmBuilder.content || '',
    };
    const next = [...utmSavedTemplates, row];
    writeUtmTemplates(workspace.workspace_id, next);
    setUtmTemplateTick((t) => t + 1);
    setUtmSaveTemplateOpen(false);
    setUtmTemplateName('');
    toast.success(`Saved template “${name}”`);
  };

  const applyUtmTemplate = (templateId) => {
    const t = utmSavedTemplates.find((x) => x.id === templateId);
    if (!t) return;
    setUtmBuilder({
      source: t.source || '',
      medium: t.medium || '',
      campaign: t.campaign || '',
      term: t.term || '',
      content: t.content || '',
    });
    setUtmTemplateTick((k) => k + 1);
    toast.success(`Loaded “${t.name}”`);
  };

  const handleCopyInsightRec = async (rec) => {
    try {
      await navigator.clipboard.writeText(rec);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy');
    }
  };

  const totalWeight = abVariants.reduce((s, v) => s + (Number(v.weight) || 0), 0);

  const handleAbVariantChange = (index, field, value) => {
    setAbVariants((prev) => prev.map((v, i) => (i === index ? { ...v, [field]: value } : v)));
  };

  const handleAbAddVariant = () => {
    if (abVariants.length >= 4) return;
    const even = Math.floor(100 / (abVariants.length + 1));
    const newVariants = [...abVariants, { url: '', weight: even }].map((v, i, arr) => ({
      ...v,
      weight: i === arr.length - 1 ? 100 - even * (arr.length - 1) : even,
    }));
    setAbVariants(newVariants);
  };

  const handleAbRemoveVariant = (index) => {
    if (abVariants.length <= 2) return;
    const remaining = abVariants.filter((_, i) => i !== index);
    const even = Math.floor(100 / remaining.length);
    setAbVariants(
      remaining.map((v, i) => ({
        ...v,
        weight: i === remaining.length - 1 ? 100 - even * (remaining.length - 1) : even,
      }))
    );
  };

  const handleAbSave = async () => {
    if (totalWeight !== 100) {
      toast.error('Variant weights must sum to 100%');
      return;
    }
    if (abVariants.some((v) => !v.url.trim())) {
      toast.error('All variant URLs are required');
      return;
    }
    setAbSaving(true);
    try {
      await abTestApi.create(linkId, { variants: abVariants });
      toast.success('A/B test saved');
      fetchAbStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save A/B test');
    } finally {
      setAbSaving(false);
    }
  };

  const handleAbDeclareWinner = async (variantIndex) => {
    if (!window.confirm(`Declare variant ${variantIndex + 1} as winner? This will update the link destination.`))
      return;
    try {
      await abTestApi.declareWinner(linkId, variantIndex);
      toast.success('Winner declared — link destination updated');
      fetchAbStats();
      const res = await api.get(`/links/${linkId}`);
      setLink(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to declare winner');
    }
  };

  const handleAbRemove = async () => {
    if (!window.confirm('Remove A/B test? This will stop the experiment.')) return;
    try {
      await abTestApi.remove(linkId);
      toast.success('A/B test removed');
      setAbStats(null);
      setAbVariants([
        { url: '', weight: 50 },
        { url: '', weight: 50 },
      ]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove A/B test');
    }
  };

  const handleRoutingRuleChange = (index, field, value) => {
    setRoutingRules((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const handleAddRoutingRule = () => {
    setRoutingRules((prev) => [...prev, { condition_type: '', condition_value: '', destination_url: '' }]);
  };

  const handleRemoveRoutingRule = (index) => {
    setRoutingRules((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMoveRoutingRule = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= routingRules.length) return;
    setRoutingRules((prev) => {
      const updated = [...prev];
      [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
      return updated;
    });
  };

  const handleRoutingConditionTypeChange = (index, nextType) => {
    setRoutingRules((prev) =>
      prev.map((r, i) => {
        if (i !== index) return r;
        let nextVal = '';
        if (nextType === 'device_and_os') nextVal = JSON.stringify({ device: 'mobile', os: 'iOS' });
        else if (nextType === 'device_type') nextVal = 'mobile';
        return { ...r, condition_type: nextType, condition_value: nextVal };
      })
    );
  };

  const handleSaveRoutingRules = async () => {
    for (const rule of routingRules) {
      if (!rule.condition_type?.trim() || !rule.destination_url?.trim()) {
        toast.error('Each rule needs a condition type and destination URL');
        return;
      }
      if (rule.condition_type === 'device_and_os') {
        try {
          const o = typeof rule.condition_value === 'string' ? JSON.parse(rule.condition_value) : rule.condition_value;
          if (!o?.device || !o?.os) {
            toast.error('Device + OS rules need both values');
            return;
          }
        } catch {
          toast.error('Invalid device + OS rule');
          return;
        }
      } else if (!String(rule.condition_value || '').trim()) {
        toast.error('Each rule needs a condition value');
        return;
      }
    }
    setRoutingSaving(true);
    try {
      await api.put(`/links/${linkId}/routing-rules`, { routing_rules: routingRules });
      toast.success('Routing rules saved');
      const res = await api.get(`/links/${linkId}`);
      setLink(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save routing rules');
    } finally {
      setRoutingSaving(false);
    }
  };

  if (loading || !link) {
    return (
      <div className="lfp-root lfld-root">
        <div className="lfp-skeleton" style={{ height: 20, width: 180, marginBottom: 12 }} />
        <div className="lfp-skeleton" style={{ height: 40, width: 360, marginBottom: 20 }} />
        <div className="lfp-skeleton" style={{ height: 180, width: '100%', borderRadius: 14 }} />
      </div>
    );
  }

  const shortUrl = getShortUrl(link);

  const scoreTone =
    scoreData &&
    (scoreData.score >= 70
      ? { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Excellent' }
      : scoreData.score >= 40
      ? { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Good' }
      : { bg: 'bg-rose-50', text: 'text-rose-600', label: 'Needs work' });

  const statusTagClass =
    link.status === 'expired'  ? 'lfp-tag lfp-coral'  :
    link.status === 'archived' ? 'lfp-tag lfp-gray'   :
                                  'lfp-tag lfp-olive';

  return (
    <div className="lfp-root lfld-root" data-testid="link-detail-page">
      <button
        type="button"
        onClick={() => navigate('/links')}
        className="lfld-back"
        data-testid="back-to-links"
      >
        <ArrowLeft size={14} weight="bold" /> Back to links
      </button>

      <div className="lfp-header lfld-header">
        <div className="lfld-header-main">
          <div className="lfp-label">Link · analytics</div>
          <h1 className="lfp-title lfld-title">
            <span className="lfld-slug-mark">/</span>
            <em>{link.slug}</em>
          </h1>
          <div className="lfld-meta-row">
            <span className={statusTagClass}>{link.status || 'active'}</span>
            {scoreData && scoreTone && (
              <span className={`lfp-tag ${
                scoreData.score >= 70 ? 'lfp-olive' : scoreData.score >= 40 ? 'lfp-mustard' : 'lfp-coral'
              }`}>
                AI score · {scoreData.score} · {scoreTone.label}
              </span>
            )}
            {link.title && <span className="lfld-meta-title">{link.title}</span>}
          </div>
          <div className="lfld-url-row">
            <code className="lfld-short-url" data-testid="short-url-display">
              {shortUrl}
            </code>
            <button
              type="button"
              onClick={handleCopy}
              className="lfp-icon-btn"
              data-testid="copy-short-url-btn"
              title="Copy short URL"
            >
              <Copy size={14} />
            </button>
          </div>
          <div className="lfld-dest-row">
            <Globe size={12} />
            <span>{link.destination_url}</span>
          </div>
          {link.tags?.length > 0 && (
            <div className="lfld-tag-row">
              {link.tags.map((t) => (
                <span key={t} className="lfp-tag lfp-gray">#{t}</span>
              ))}
            </div>
          )}
        </div>
        <div className="lfld-header-actions">
          <button
            type="button"
            className="lfp-btn lfp-btn-outline"
            onClick={() => setShowQR(true)}
            data-testid="qr-btn"
          >
            <QrCode size={14} weight="bold" /> QR
          </button>
          <button
            type="button"
            className="lfp-btn lfp-btn-outline"
            onClick={() => setShowEdit(true)}
            data-testid="edit-link-btn"
          >
            <PencilSimple size={14} weight="bold" /> Edit
          </button>
          <button
            type="button"
            className="lfp-btn lfp-btn-danger"
            onClick={handleDelete}
            data-testid="delete-link-btn"
          >
            <Trash size={14} weight="bold" />
          </button>
        </div>
      </div>

      <div className="space-y-4">
      {(scoreLoading || (scoreData != null && Number.isFinite(scoreData.score))) && (
        <div className="kinetic-card bg-card rounded-xl border border-border p-6 relative overflow-hidden ambient-shadow">
          <div className="absolute top-0 left-0 right-0 h-1 ai-shimmer-bar opacity-75 rounded-t-xl" />
          <div className="flex items-center justify-between mb-4">
            <span className="text-[10px] font-bold tracking-wider text-muted-foreground uppercase">
              AI performance score
            </span>
            <Lightbulb size={18} className="text-primary" weight="fill" />
          </div>
          {scoreLoading ? (
            <div className="space-y-4 animate-pulse" aria-busy aria-label="Loading AI score">
              <div className="flex gap-3 items-end">
                <div className="h-14 sm:h-16 w-24 rounded-lg bg-muted" />
                <div className="h-6 w-16 rounded bg-muted pb-1" />
                <div className="ml-auto h-8 w-20 rounded-full bg-muted" />
              </div>
              <div className="pt-4 border-t border-border/80 space-y-2">
                <div className="h-4 w-32 rounded bg-muted" />
                <div className="h-16 w-full rounded-lg bg-muted" />
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-end gap-3">
                <span className="text-5xl sm:text-6xl font-black leading-none stitch-gradient-text">
                  {scoreData.score}
                </span>
                <span className="text-muted-foreground text-lg pb-1">/100</span>
                {scoreTone && (
                  <span className={`chip mb-1 ml-auto ${scoreTone.bg} ${scoreTone.text}`}>
                    {scoreTone.label}
                  </span>
                )}
              </div>
              <div className="mt-6 pt-4 border-t border-border/80 space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">Kinetic insights</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {scoreData.tips?.[0] ||
                      'Share this link across channels to build a stronger engagement signal.'}
                  </p>
                </div>
                {scoreData?.tips && scoreData.tips.length > 1 && (
                  <div className="rounded-xl border border-amber-200/80 bg-amber-50/50 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="grid place-items-center h-7 w-7 rounded-lg bg-amber-100 text-amber-600">
                        <Lightbulb size={14} weight="fill" />
                      </span>
                      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-800">
                        More improvement tips
                      </span>
                    </div>
                    <ul className="space-y-1.5">
                      {scoreData.tips.slice(1).map((tip, i) => (
                        <li key={i} className="text-sm text-foreground flex items-start gap-2">
                          <span className="text-amber-600 mt-0.5">+</span> {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              {scoreData && !scoreData.tips?.length && scoreData.score != null && (
                <p className="mt-4 text-sm text-muted-foreground rounded-lg border border-border/80 bg-muted/20 px-4 py-3">
                  Detailed tips will appear as we analyse more signals for this link.
                </p>
              )}
            </>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total clicks', value: analytics?.total_clicks || 0, icon: CursorClick, accent: 'bg-primary/10 text-primary' },
          { label: 'Unique visitors', value: analytics?.unique_visitors || 0, icon: Users, accent: 'bg-emerald-50 text-emerald-600' },
          { label: 'QR scans', value: analytics?.sources?.find((s) => s.name === 'qr')?.value || 0, icon: QrCode, accent: 'bg-amber-50 text-amber-600' },
          { label: 'Created', value: link.created_at?.slice(0, 10), icon: Clock, accent: 'bg-muted text-foreground' },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="surface-card p-5 ambient-shadow border-border/90 kinetic-card">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  {s.label}
                </span>
                <span className={`grid place-items-center h-8 w-8 rounded-xl ${s.accent}`}>
                  <Icon size={14} weight="fill" />
                </span>
              </div>
              <p className="font-display text-2xl font-extrabold text-foreground tracking-tight">
                {typeof s.value === 'number' ? s.value.toLocaleString() : s.value}
              </p>
            </div>
          );
        })}
      </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2.5">
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger
            className="h-9 w-[140px] rounded-xl border-border bg-white text-sm"
            data-testid="analytics-date-range"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="24h">Last 24 hours</SelectItem>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="90d">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
        <Select value={granularity} onValueChange={setGranularity}>
          <SelectTrigger
            className="h-9 w-[120px] rounded-xl border-border bg-white text-sm"
            data-testid="analytics-granularity"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="hour">Hourly</SelectItem>
            <SelectItem value="day">Daily</SelectItem>
            <SelectItem value="week">Weekly</SelectItem>
            <SelectItem value="month">Monthly</SelectItem>
          </SelectContent>
        </Select>
        <Button
          onClick={handleExport}
          variant="outline"
          className="ml-auto rounded-xl border-border h-9 text-sm gap-1.5"
          data-testid="export-btn"
        >
          <DownloadSimple size={14} weight="bold" /> Export CSV
        </Button>
      </div>

      <Tabs value={detailTab} onValueChange={setDetailTab} className="space-y-5">
        <TabsList className="bg-white border border-border rounded-2xl p-1 h-auto flex flex-wrap gap-1">
          {[
            ['overview', 'Overview', null],
            ['audience', 'Audience', Eye],
            ['devices', 'Devices', null],
            ['referrers', 'Referrers', null],
            ['history', 'History', null],
            ['live', 'Live', WifiHigh],
            ['insights', 'AI Insights', Sparkle],
            ['ab-test', 'A/B test', Flask],
            ['utm', 'UTM', null],
            ['routing', 'Routing', Signpost],
            ['alerts', 'Alerts', Bell],
            ['health', 'Health', Heart],
          ].map(([value, label, Icon]) => (
            <TabsTrigger
              key={value}
              value={value}
              data-testid={`${value}-tab`}
              className="rounded-xl px-3.5 py-1.5 text-xs font-semibold text-muted-foreground data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-glow"
            >
              <span className="inline-flex items-center gap-1.5">
                {value === 'live' && <span className="lfld-tab-live-dot" aria-hidden />}
                {Icon && <Icon size={13} weight="bold" />}
                {label}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="space-y-5">
          <div className="lfld-grid-2">
            <div className="surface-card lfld-mock-panel">
              <div className="lfld-panel-head">
                <div>
                  <div className="lfld-panel-title">Click pulse</div>
                  <div className="lfld-panel-sub">Hits across the selected window</div>
                </div>
                <button type="button" className="lfld-panel-action" onClick={handleExport}>
                  Export raw →
                </button>
              </div>
              <div className="lfld-legend-row">
                <span className="lfld-legend-item">
                  <span className="lfld-legend-dot" style={{ background: 'var(--coral)' }} />
                  Total clicks
                </span>
                <span className="lfld-legend-item">
                  <span className="lfld-legend-dot" style={{ background: 'var(--olive)' }} />
                  Unique visitors
                </span>
              </div>
              <div className="lfld-chart-wrap">
                {analytics?.clicks_over_time?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={analytics.clicks_over_time}>
                      <defs>
                        <linearGradient id="lfldOverviewGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#ff5436" stopOpacity={0.18} />
                          <stop offset="100%" stopColor="#ff5436" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 6" stroke="rgba(15,16,32,0.06)" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: '#6B6F86' }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(v) => v?.slice(5)}
                      />
                      <YAxis tick={{ fontSize: 11, fill: '#6B6F86' }} tickLine={false} axisLine={false} />
                      <Tooltip {...TooltipChrome} />
                      <Area
                        type="monotone"
                        dataKey="clicks"
                        stroke="#ff5436"
                        strokeWidth={2.5}
                        fill="url(#lfldOverviewGrad)"
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="unique_visitors"
                        stroke="#8b9b3f"
                        strokeWidth={2}
                        dot={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full grid place-items-center text-sm" style={{ color: 'var(--ink-dim)' }}>
                    No click data in this period
                  </div>
                )}
              </div>
            </div>

            <div className="surface-card lfld-mock-panel">
              <div className="lfld-panel-head">
                <div>
                  <div className="lfld-panel-title">Top regions</div>
                  <div className="lfld-panel-sub">
                    {dateRange === '24h'
                      ? 'Last 24 hours'
                      : dateRange === '7d'
                        ? 'Last 7 days'
                        : dateRange === '90d'
                          ? 'Last 90 days'
                          : 'Last 30 days'}
                  </div>
                </div>
              </div>
              <div className="lfld-bar-list">
                {analytics?.countries?.length > 0 ? (
                  (() => {
                    const list = analytics.countries.slice(0, 6);
                    const topVal = Math.max(...list.map((c) => c.value), 1);
                    const tot = list.reduce((s, c) => s + c.value, 0);
                    return list.map((c, i) => {
                      const pct = tot > 0 ? Math.round((c.value / tot) * 100) : 0;
                      const scale = c.value / topVal;
                      const vf = barFillVariant(i);
                      return (
                        <div key={c.name} className="lfld-bar-row">
                          <span className="lfld-bar-icon">
                            <CountryFlagMark countryName={c.name} />
                          </span>
                          <div className="lfld-bar-info">
                            <div className="lfld-bar-name">{c.name}</div>
                            <div className="lfld-bar-track">
                              <div className={`lfld-bar-fill ${vf}`.trim()} style={{ ['--target']: scale }} />
                            </div>
                          </div>
                          <div className="lfld-bar-num">
                            {c.value.toLocaleString()} <span className="pct">{pct}%</span>
                          </div>
                        </div>
                      );
                    });
                  })()
                ) : (
                  <p className="text-sm" style={{ color: 'var(--ink-dim)' }}>No country data yet</p>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="devices" className="space-y-5">
          <header className="lfld-tab-section-head">
            <div>
              <h2 className="lfld-tab-section-title">
                What they&apos;re <em>using</em>.
              </h2>
              <p className="lfld-tab-section-sub">Devices, operating systems, and browsers.</p>
            </div>
          </header>
          <div className="lfld-grid-3">
            <div className="surface-card lfld-mock-panel">
              <div className="lfld-panel-head">
                <div>
                  <div className="lfld-panel-title">Devices</div>
                  <div className="lfld-panel-sub">By click count</div>
                </div>
              </div>
              <LfldPieDonutBlock rows={analytics?.devices} centerLine1={analytics?.total_clicks ?? 0} centerLine2="clicks" />
            </div>
            <div className="surface-card lfld-mock-panel">
              <div className="lfld-panel-head">
                <div>
                  <div className="lfld-panel-title">Operating systems</div>
                  <div className="lfld-panel-sub">Top 5</div>
                </div>
              </div>
              <div className="lfld-bar-list">
                {analytics?.os?.length > 0 ? (
                  (() => {
                    const list = analytics.os.slice(0, 5);
                    const total = list.reduce((s, d) => s + d.value, 0);
                    const topVal = Math.max(...list.map((d) => d.value), 1);
                    return list.map((row, i) => {
                      const pct = total > 0 ? Math.round((row.value / total) * 100) : 0;
                      return (
                        <div key={row.name} className="lfld-bar-row">
                          <span className="lfld-bar-icon lfld-bar-icon-muted" aria-hidden />
                          <div className="lfld-bar-info">
                            <div className="lfld-bar-name">{row.name}</div>
                            <div className="lfld-bar-track">
                              <div
                                className={`lfld-bar-fill ${barFillVariant(i)}`.trim()}
                                style={{ ['--target']: row.value / topVal }}
                              />
                            </div>
                          </div>
                          <div className="lfld-bar-num">{pct}%</div>
                        </div>
                      );
                    });
                  })()
                ) : (
                  <p className="text-sm" style={{ color: 'var(--ink-dim)' }}>No data</p>
                )}
              </div>
            </div>
            <div className="surface-card lfld-mock-panel">
              <div className="lfld-panel-head">
                <div>
                  <div className="lfld-panel-title">Browsers</div>
                  <div className="lfld-panel-sub">Top 5</div>
                </div>
              </div>
              <div className="lfld-bar-list">
                {analytics?.browsers?.length > 0 ? (
                  (() => {
                    const list = analytics.browsers.slice(0, 5);
                    const total = list.reduce((s, d) => s + d.value, 0);
                    const topVal = Math.max(...list.map((d) => d.value), 1);
                    return list.map((row, i) => {
                      const pct = total > 0 ? Math.round((row.value / total) * 100) : 0;
                      return (
                        <div key={row.name} className="lfld-bar-row">
                          <span className="lfld-bar-icon lfld-bar-icon-muted" aria-hidden />
                          <div className="lfld-bar-info">
                            <div className="lfld-bar-name">{row.name}</div>
                            <div className="lfld-bar-track">
                              <div
                                className={`lfld-bar-fill ${barFillVariant(i)}`.trim()}
                                style={{ ['--target']: row.value / topVal }}
                              />
                            </div>
                          </div>
                          <div className="lfld-bar-num">{pct}%</div>
                        </div>
                      );
                    });
                  })()
                ) : (
                  <p className="text-sm" style={{ color: 'var(--ink-dim)' }}>No data</p>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="audience" className="space-y-5">
          <header className="lfld-tab-section-head">
            <div>
              <h2 className="lfld-tab-section-title">
                Who&apos;s <em>clicking</em>.
              </h2>
              <p className="lfld-tab-section-sub">
                New vs returning, geography, and language preferences.
              </p>
            </div>
          </header>

          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {includeBots ? 'Showing all traffic including bots' : 'Showing human traffic only'}
            </p>
            <button
              type="button"
              onClick={() => setIncludeBots((v) => !v)}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
                includeBots
                  ? 'bg-amber-50 border-amber-200 text-amber-700'
                  : 'bg-muted border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              <Eye size={12} weight="bold" />
              {includeBots ? 'Hide bot traffic' : 'Include bot traffic'}
            </button>
          </div>

          <div className="lfld-grid-2-equal">
            <div className="surface-card lfld-mock-panel">
              <div className="lfld-panel-head">
                <div>
                  <div className="lfld-panel-title">New vs returning</div>
                  <div className="lfld-panel-sub">Unique IPs in this period</div>
                </div>
              </div>
              {(() => {
                const newV = analytics?.audience?.new_visitor_ips ?? 0;
                const retV = analytics?.audience?.returning_visitor_ips ?? 0;
                const tot = newV + retV;
                if (tot === 0) {
                  return (
                    <p className="text-sm px-[22px] pb-[22px]" style={{ color: 'var(--ink-dim)' }}>
                      No visitor data yet
                    </p>
                  );
                }
                const newPct = Math.round((newV / tot) * 100);
                return (
                  <LfldPieDonutBlock
                    rows={[
                      { name: 'New visitors', value: newV },
                      { name: 'Returning visitors', value: retV },
                    ]}
                    centerLine1={`${newPct}%`}
                    centerLine2="NEW"
                    valueLabel="visitors"
                  />
                );
              })()}
            </div>
            <div className="surface-card lfld-mock-panel">
              <div className="lfld-panel-head">
                <div>
                  <div className="lfld-panel-title">Languages</div>
                  <div className="lfld-panel-sub">Inferred from geo</div>
                </div>
              </div>
              <div className="lfld-bar-list">
                {(() => {
                  const langList = inferLanguagesFromCountries(analytics?.countries);
                  if (langList.length === 0) {
                    return (
                      <p className="text-sm px-[22px] pb-[22px]" style={{ color: 'var(--ink-dim)' }}>
                        No language data yet
                      </p>
                    );
                  }
                  const slice = langList.slice(0, 8);
                  const topVal = Math.max(...slice.map((row) => row.value), 1);
                  return slice.map((row, i) => (
                    <div key={row.name} className="lfld-bar-row">
                      <span className="lfld-bar-icon" aria-hidden>
                        {row.code}
                      </span>
                      <div className="lfld-bar-info">
                        <div className="lfld-bar-name">{row.name}</div>
                        <div className="lfld-bar-track">
                          <div
                            className={`lfld-bar-fill ${barFillVariant(i)}`.trim()}
                            style={{ ['--target']: row.value / topVal }}
                          />
                        </div>
                      </div>
                      <div className="lfld-bar-num">
                        {row.value.toLocaleString()} <span className="pct">{row.pct}%</span>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>

          {(() => {
            const ps = analytics?.privacy_signals;
            if (!ps || ps.total === 0) {
              return (
                <div className="surface-card lfld-mock-panel p-6">
                  <h3 className="font-display text-base font-bold text-foreground mb-1">Traffic quality</h3>
                  <p className="text-xs text-muted-foreground">No data in this period</p>
                </div>
              );
            }
            const pct = (n) => (ps.total > 0 ? Math.round((n / ps.total) * 100) : 0);
            const stats = [
              { label: 'Human', value: ps.human, pct: pct(ps.human), color: '#3CC68C' },
              { label: 'Bot / crawler', value: ps.bot, pct: pct(ps.bot), color: '#F26C6C' },
              { label: 'Datacenter / hosting', value: ps.datacenter, pct: pct(ps.datacenter), color: '#F5A524' },
              { label: 'VPN', value: ps.vpn, pct: pct(ps.vpn), color: '#4648d4' },
              { label: 'Proxy', value: ps.proxy, pct: pct(ps.proxy), color: '#8B87FF' },
              { label: 'Tor', value: ps.tor, pct: pct(ps.tor), color: '#6B6F86' },
            ];
            return (
              <div className="surface-card lfld-mock-panel p-6">
                <div className="lfld-panel-head" style={{ marginBottom: 16 }}>
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={16} weight="bold" className="text-primary" />
                    <div>
                      <div className="lfld-panel-title">Traffic quality</div>
                      <div className="lfld-panel-sub">{ps.total.toLocaleString()} sampled clicks</div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-5">
                  {stats.map((s) => (
                    <div key={s.label} className="rounded-xl border border-border p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-muted-foreground">{s.label}</span>
                        <span className="text-xs font-mono font-semibold" style={{ color: s.color }}>
                          {s.pct}%
                        </span>
                      </div>
                      <div className="text-lg font-bold text-foreground font-display">{s.value.toLocaleString()}</div>
                      <div className="mt-2 h-1 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${s.pct}%`, backgroundColor: s.color }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          <div className="surface-card lfld-mock-panel">
            <div className="lfld-panel-head">
              <div className="flex items-center gap-2">
                <MapPin size={16} weight="bold" className="text-primary" />
                <div>
                  <div className="lfld-panel-title">Top cities</div>
                  <div className="lfld-panel-sub">Down to the city level</div>
                </div>
              </div>
            </div>
            <div className="lfld-bar-list">
              {analytics?.cities?.length > 0 ? (
                (() => {
                  const list = analytics.cities.slice(0, 10);
                  const total = list.reduce((s, c) => s + c.value, 0);
                  const topVal = Math.max(...list.map((c) => c.value), 1);
                  return list.map((city, i) => {
                    const pct = total > 0 ? Math.round((city.value / total) * 1000) / 10 : 0;
                    const cName = resolveCountryDisplayName(city.country);
                    const label = cName ? `${city.name} · ${cName}` : city.name;
                    return (
                      <div key={`${city.name}-${i}`} className="lfld-bar-row">
                        <span className="lfld-bar-icon">
                          <CountryFlagMark countryName={cName || city.country} />
                        </span>
                        <div className="lfld-bar-info">
                          <div className="lfld-bar-name">{label}</div>
                          <div className="lfld-bar-track">
                            <div
                              className={`lfld-bar-fill ${barFillVariant(i)}`.trim()}
                              style={{ ['--target']: city.value / topVal }}
                            />
                          </div>
                        </div>
                        <div className="lfld-bar-num">
                          {city.value.toLocaleString()} <span className="pct">{pct}%</span>
                        </div>
                      </div>
                    );
                  });
                })()
              ) : (
                <p className="text-sm px-[22px] pb-[22px]" style={{ color: 'var(--ink-dim)' }}>
                  No city data yet — city is populated from ipinfo.io enrichment on each click.
                </p>
              )}
            </div>
          </div>

          {/* ── Company Attribution ── */}
          <div className="surface-card lfld-mock-panel p-6">
            <div className="flex items-center gap-2 mb-5">
              <Buildings size={16} weight="bold" className="text-primary" />
              <h3 className="font-display text-base font-bold text-foreground">Company attribution</h3>
              <span className="ml-1 text-xs text-muted-foreground">(datacenter IPs excluded)</span>
            </div>
            {analytics?.companies?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left text-muted-foreground font-medium pb-2 pr-4">Company</th>
                      <th className="text-left text-muted-foreground font-medium pb-2 pr-4">Domain</th>
                      <th className="text-right text-muted-foreground font-medium pb-2">Clicks</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {analytics.companies.map((co) => (
                      <tr key={co.domain || co.name} className="hover:bg-muted/40 transition-colors">
                        <td className="py-2 pr-4 font-medium text-foreground">{co.name}</td>
                        <td className="py-2 pr-4 text-muted-foreground">
                          {co.domain ? (
                            <span className="flex items-center gap-1.5">
                              <img
                                src={`https://www.google.com/s2/favicons?domain=${co.domain}&sz=16`}
                                alt=""
                                className="w-3.5 h-3.5 rounded-sm"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              {co.domain}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="py-2 text-right font-mono font-semibold text-foreground">{co.count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No company data yet — requires ipinfo.io paid plan for company intelligence.</p>
            )}
          </div>

          <div className="surface-card lfld-mock-panel">
            <div className="lfld-panel-head">
              <div>
                <div className="lfld-panel-title">Geography</div>
                <div className="lfld-panel-sub">Click intensity by country</div>
              </div>
            </div>
            <div className="px-4 pb-4">
              <WorldClickMap data={analytics?.countries || []} height={320} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="referrers" className="space-y-5">
          <header className="lfld-tab-section-head">
            <div>
              <h2 className="lfld-tab-section-title">
                Where they&apos;re <em>coming from</em>.
              </h2>
              <p className="lfld-tab-section-sub">Sources, channels, and referring domains.</p>
            </div>
          </header>

          <div className="lfld-grid-2-equal">
            <div className="surface-card lfld-mock-panel">
              <div className="lfld-panel-head">
                <div>
                  <div className="lfld-panel-title">Top referring domains</div>
                  <div className="lfld-panel-sub">By click volume</div>
                </div>
              </div>
              <div className="lfld-bar-list">
                {analytics?.referrers?.length > 0 ? (
                  (() => {
                    const list = analytics.referrers.slice(0, 10);
                    const total = list.reduce((s, r) => s + r.value, 0);
                    const topVal = Math.max(...list.map((r) => r.value), 1);
                    return list.map((row, i) => {
                      const host = refererHostname(row.name);
                      const pct = total > 0 ? Math.round((row.value / total) * 1000) / 10 : 0;
                      return (
                        <div key={row.name} className="lfld-bar-row">
                          <ReferrerFaviconIcon rawName={row.name} />
                          <div className="lfld-bar-info">
                            <div
                              className="lfld-bar-name"
                              style={
                                host === '(direct)' ? { fontStyle: 'italic', color: 'var(--ink-faint)' } : undefined
                              }
                            >
                              {host}
                            </div>
                            <div className="lfld-bar-track">
                              <div
                                className={`lfld-bar-fill ${barFillVariant(i)}`.trim()}
                                style={{ ['--target']: row.value / topVal }}
                              />
                            </div>
                          </div>
                          <div className="lfld-bar-num">
                            {row.value.toLocaleString()} <span className="pct">{pct}%</span>
                          </div>
                        </div>
                      );
                    });
                  })()
                ) : (
                  <p className="text-sm px-[22px] pb-[22px]" style={{ color: 'var(--ink-dim)' }}>
                    No referrer data
                  </p>
                )}
              </div>
            </div>
            <div className="surface-card lfld-mock-panel">
              <div className="lfld-panel-head">
                <div>
                  <div className="lfld-panel-title">Channels</div>
                  <div className="lfld-panel-sub">From UTM source / medium / referrer</div>
                </div>
              </div>
              <LfldPieDonutBlock
                rows={analytics?.channels}
                centerLine1={analytics?.total_clicks ?? 0}
                centerLine2="clicks"
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="history" className="space-y-5">
          <header className="lfld-tab-section-head">
            <div>
              <h2 className="lfld-tab-section-title">
                Link <em>history</em>.
              </h2>
              <p className="lfld-tab-section-sub">Edits, QR exports, UTM changes, and alerts.</p>
            </div>
          </header>
          <div className="surface-card lfld-mock-panel">
            <div className="lfld-panel-head">
              <div>
                <div className="lfld-panel-title">Activity timeline</div>
                <div className="lfld-panel-sub">Most recent first</div>
              </div>
            </div>
            {link.change_history?.length > 0 ? (
              <>
                <div className="flex flex-wrap gap-1.5 px-[22px] pb-3">
                  {[
                    ['all', 'All'],
                    ['utm', 'UTM'],
                    ['edit', 'Edits'],
                    ['qr', 'QR'],
                    ['alert', 'Alerts'],
                    ['misc', 'Other'],
                  ].map(([id, lbl]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setHistoryFilter(id)}
                      className={`text-[10px] font-semibold px-2.5 py-1 rounded-lg border transition-colors ${
                        historyFilter === id
                          ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                          : 'border-border/80 bg-muted/40 text-muted-foreground hover:bg-muted/70'
                      }`}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
                {(link.change_history || [])
                  .slice()
                  .reverse()
                  .filter((entry) => matchesHistoryFilter(entry, historyFilter)).length > 0 ? (
                  <div className="lfld-tl">
                    {(link.change_history || [])
                      .slice()
                      .reverse()
                      .filter((entry) => matchesHistoryFilter(entry, historyFilter))
                      .map((entry, i) => {
                    const pairs = Object.entries(entry.changes || {});
                    const firstKey = pairs[0]?.[0] || 'update';
                    const kind = classifyHistoryEntry(entry);
                    const title =
                      pairs.length === 1 ? (
                        <>
                          <span className="font-medium">{pairs[0][0]}</span> updated to{' '}
                          <em>{String(pairs[0][1]?.to ?? '').slice(0, 120)}</em>
                        </>
                      ) : (
                        <>
                          {pairs.length} field{pairs.length === 1 ? '' : 's'} updated —{' '}
                          <em>{pairs.map(([k]) => k).join(', ')}</em>
                        </>
                      );
                    return (
                      <div key={i} className="lfld-tl-event">
                        <div className="lfld-tl-time">
                          {entry.timestamp?.slice(0, 10) || '—'}
                          <span className="lfld-tl-rel">
                            {entry.timestamp?.slice(11, 16) || ''} · {entry.actor_name || entry.actor || 'system'}
                          </span>
                        </div>
                        <div className="lfld-tl-marker">
                          <div className={`lfld-tl-dot ${historyTimelineDotClass(i)}`.trim()} />
                          <div className="lfld-tl-line" />
                        </div>
                        <div className="lfld-tl-content">
                          <div className="lfld-tl-title">{title}</div>
                          {pairs.length === 1 ? (
                            <div className="lfld-tl-meta flex flex-wrap items-center gap-2">
                              <span
                                className={`text-[10px] font-semibold px-2 py-0.5 rounded-md uppercase tracking-wide ${kind.cls}`}
                              >
                                {kind.label}
                              </span>
                              <span className="text-[10px] font-mono font-semibold text-muted-foreground">
                                {firstKey.replace(/_/g, ' ')}
                              </span>
                              {pairs[0][1]?.from != null && (
                                <span className="line-through text-rose-500/90">
                                  {String(pairs[0][1].from).slice(0, 80)}
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="lfld-tl-meta space-y-1.5 mt-2">
                              {pairs.map(([field, change]) => (
                                <div key={field} className="text-xs text-muted-foreground">
                                  <span className="font-semibold text-foreground">{field}</span>:{' '}
                                  <span className="text-rose-500 line-through">{String(change?.from)?.slice(0, 60)}</span>{' '}
                                  →{' '}
                                  <span className="text-emerald-600">{String(change?.to)?.slice(0, 60)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                      })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground px-[22px] pb-[22px]">No events match this filter.</p>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground px-[22px] pb-[22px]">No changes recorded</p>
            )}
          </div>
        </TabsContent>

        {/* Live feed */}
        <TabsContent value="live" className="space-y-5">
          <header className="lfld-tab-section-head">
            <div>
              <h2 className="lfld-tab-section-title">
                Streaming <em>now</em>.
              </h2>
              <p className="lfld-tab-section-sub">Every click on this link as it happens.</p>
            </div>
          </header>

          <div className="surface-card lfld-mock-panel">
            <div className="lfld-panel-head">
              <div>
                <div className="lfld-panel-title">Realtime click feed</div>
                <div className="lfld-panel-sub">Recent events · live updates</div>
              </div>
              <div className="legend" style={{ padding: 0, border: 'none' }}>
                <div className="legend-item inline-flex items-center gap-2 text-xs text-muted-foreground">
                  <span
                    className="legend-dot lfld-tab-live-dot"
                    style={{ verticalAlign: 'middle' }}
                    aria-hidden
                  />
                  Live
                </div>
              </div>
            </div>
            <LiveClickFeed linkId={link.link_id} maxItems={18} layout="mock" />
          </div>
        </TabsContent>

        {/* AI Insights */}
        <TabsContent value="insights">
          <div className="surface-card p-6 space-y-5">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-base font-bold text-foreground inline-flex items-center gap-2">
                  <Sparkle size={16} weight="fill" className="text-primary" />
                  AI Click Insights
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Claude analyses your click data and surfaces actionable recommendations.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={insightsRange}
                  onChange={(e) => setInsightsRange(e.target.value)}
                  className="input-modern text-xs py-1.5 h-8 w-24"
                >
                  <option value="7d">7 days</option>
                  <option value="30d">30 days</option>
                  <option value="90d">90 days</option>
                </select>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1.5 text-xs"
                  onClick={() => fetchInsights(insightsRange)}
                  disabled={insightsLoading}
                >
                  {insightsLoading ? (
                    <ArrowClockwise size={13} className="animate-spin" />
                  ) : (
                    <Sparkle size={13} weight="fill" />
                  )}
                  {aiInsights ? 'Refresh' : 'Generate'}
                </Button>
              </div>
            </div>

            {!aiInsights && !insightsLoading && (
              <div className="flex flex-col items-center justify-center py-14 gap-3 text-muted-foreground">
                <Sparkle size={36} className="opacity-20" />
                <p className="text-sm">Click "Generate" to get AI-powered insights for this link.</p>
              </div>
            )}

            {insightsLoading && (
              <div className="flex flex-col items-center justify-center py-14 gap-3 text-muted-foreground">
                <ArrowClockwise size={28} className="animate-spin opacity-40" />
                <p className="text-sm">Claude is analysing your click data…</p>
              </div>
            )}

            {aiInsights && !insightsLoading && (() => {
              const sentimentIcon = {
                positive: <CheckCircle size={16} className="text-emerald-500" weight="fill" />,
                neutral: <MinusCircle size={16} className="text-amber-500" weight="fill" />,
                needs_attention: <Warning size={16} className="text-rose-500" weight="fill" />,
              }[aiInsights.sentiment] || null;

              return (
                <div className="space-y-4">
                  <div className="rounded-2xl border border-emerald-900/35 bg-[linear-gradient(145deg,#0b1f1c_0%,#14302a_48%,#0d2420_100%)] p-5 shadow-inner text-emerald-50/95">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-400/85 mb-2">
                      What Claude noticed
                    </p>
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5">{sentimentIcon}</span>
                      <p className="text-[15px] font-semibold leading-snug text-white">{aiInsights.headline}</p>
                    </div>
                    <button
                      type="button"
                      className="mt-4 text-[11px] font-semibold text-emerald-200/90 underline-offset-4 hover:text-white hover:underline"
                      onClick={() => aiInsights.headline && handleCopyInsightRec(aiInsights.headline)}
                    >
                      Copy headline
                    </button>
                  </div>

                  {/* Key signals */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { label: 'Peak time', value: aiInsights.peak_time },
                      { label: 'Primary audience', value: aiInsights.top_audience },
                      { label: 'Top source', value: aiInsights.top_referrer_insight },
                    ].map(({ label, value }) => (
                      <div key={label} className="rounded-xl border border-border bg-muted/30 p-3">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
                        <p className="text-sm font-semibold text-foreground">{value || '—'}</p>
                      </div>
                    ))}
                  </div>

                  {/* Recommendations */}
                  {aiInsights.recommendations?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                        Recommendations
                      </p>
                      <ul className="space-y-3">
                        {aiInsights.recommendations.map((rec, i) => {
                          const next = insightRecNextTab(rec);
                          return (
                            <li
                              key={i}
                              className="rounded-xl border border-border bg-card p-3 text-sm text-foreground"
                            >
                              <div className="flex items-start gap-2">
                                <span className="mt-0.5 h-4 w-4 flex-shrink-0 rounded-full bg-primary/15 text-primary grid place-items-center text-[10px] font-bold">
                                  {i + 1}
                                </span>
                                <span className="flex-1 leading-relaxed">{rec}</span>
                              </div>
                              <div className="flex flex-wrap gap-2 mt-3 pl-6">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-8 rounded-lg text-xs gap-1"
                                  onClick={() => handleCopyInsightRec(rec)}
                                >
                                  <Copy size={12} /> Copy text
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  className="h-8 rounded-lg text-xs gap-1 gradient-primary text-white shadow-glow"
                                  onClick={() => setDetailTab(next)}
                                >
                                  Open{' '}
                                  {next === 'utm'
                                    ? 'UTM'
                                    : next === 'audience'
                                    ? 'Audience'
                                    : next === 'devices'
                                    ? 'Devices'
                                    : next === 'referrers'
                                    ? 'Referrers'
                                    : 'Overview'}
                                </Button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {/* Raw aggregates: top countries mini-chart */}
                  {aiInsights.aggregates?.topCountries?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                        Top countries ({aiInsights.aggregates.total} clicks)
                      </p>
                      <div className="space-y-1.5">
                        {aiInsights.aggregates.topCountries.slice(0, 5).map(([country, count]) => {
                          const pct = Math.round((count / aiInsights.aggregates.total) * 100);
                          return (
                            <div key={country} className="flex items-center gap-2 text-xs">
                              <span className="w-24 text-foreground font-medium truncate">{country}</span>
                              <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-primary"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className="w-8 text-right text-muted-foreground">{pct}%</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </TabsContent>

        {/* A/B test */}
        <TabsContent value="ab-test" data-testid="ab-test-tab-content">
          <div className="space-y-5">
            <div className="surface-card p-6">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h3 className="font-display text-base font-bold text-foreground inline-flex items-center gap-2">
                    <Flask size={16} weight="fill" className="text-primary" />
                    Variant setup
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Split traffic across destination variants and let stats guide a winner.
                  </p>
                </div>
                {abVariants.length < 4 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAbAddVariant}
                    className="rounded-xl border-border text-xs gap-1.5"
                    data-testid="ab-add-variant-btn"
                  >
                    <Plus size={12} weight="bold" /> Add variant
                  </Button>
                )}
              </div>

              <div className="space-y-4">
                {abVariants.map((variant, i) => (
                  <div
                    key={i}
                    className="rounded-2xl border border-border bg-muted/30 p-5"
                    data-testid={`ab-variant-${i}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="chip bg-primary/10 text-primary">
                        Variant {String.fromCharCode(65 + i)}
                      </span>
                      {abVariants.length > 2 && (
                        <button
                          onClick={() => handleAbRemoveVariant(i)}
                          className="text-muted-foreground hover:text-rose-500 transition-colors"
                          data-testid={`ab-remove-variant-${i}`}
                        >
                          <X size={14} weight="bold" />
                        </button>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-1.5 block">
                          Destination URL
                        </label>
                        <Input
                          value={variant.url}
                          onChange={(e) => handleAbVariantChange(i, 'url', e.target.value)}
                          placeholder="https://example.com/page"
                          className="rounded-xl border-border bg-white"
                          data-testid={`ab-variant-url-${i}`}
                        />
                      </div>
                      <div>
                        <div className="flex justify-between text-xs mb-1.5">
                          <label className="font-semibold uppercase tracking-[0.14em] text-muted-foreground text-[11px]">
                            Traffic weight
                          </label>
                          <span
                            className="font-mono text-foreground font-semibold"
                            data-testid={`ab-variant-weight-${i}`}
                          >
                            {variant.weight}%
                          </span>
                        </div>
                        <Slider
                          value={[variant.weight]}
                          onValueChange={([val]) => handleAbVariantChange(i, 'weight', val)}
                          min={1}
                          max={99}
                          step={1}
                          className="w-full"
                          data-testid={`ab-variant-slider-${i}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between mt-5">
                <span
                  className={`chip ${
                    totalWeight === 100 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-600'
                  }`}
                  data-testid="ab-weight-total"
                >
                  Total: {totalWeight}% {totalWeight !== 100 && '· must equal 100%'}
                </span>
                <Button
                  onClick={handleAbSave}
                  disabled={abSaving || totalWeight !== 100}
                  className="rounded-xl gradient-primary text-white shadow-glow h-10 text-sm font-semibold px-5"
                  data-testid="ab-save-btn"
                >
                  {abSaving ? 'Saving…' : 'Save A/B test'}
                </Button>
              </div>
            </div>

            {abStats?.enabled && (
              <div className="surface-card p-6" data-testid="ab-stats-section">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-display text-base font-bold text-foreground inline-flex items-center gap-2">
                    <Flask size={16} weight="fill" className="text-primary" /> Live results
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAbRemove}
                    className="rounded-xl border-rose-200 text-rose-500 hover:text-rose-600 hover:bg-rose-50 text-xs gap-1"
                    data-testid="ab-remove-btn"
                  >
                    <Trash size={12} weight="bold" /> Remove test
                  </Button>
                </div>

                <div className="space-y-3">
                  {abStats.variants?.map((variant, i) => (
                    <div
                      key={i}
                      className="rounded-2xl border border-border p-5"
                      data-testid={`ab-stat-variant-${i}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="chip bg-primary/10 text-primary">
                              Variant {String.fromCharCode(65 + i)}
                            </span>
                            {variant.is_winner && (
                              <span
                                className="chip bg-emerald-50 text-emerald-700"
                                data-testid={`ab-winner-badge-${i}`}
                              >
                                <Trophy size={10} weight="fill" /> Winner
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate">{variant.url}</p>
                          <div className="flex flex-wrap gap-4 mt-2 text-xs text-muted-foreground">
                            <span>
                              <span
                                className="font-mono font-semibold text-foreground"
                                data-testid={`ab-clicks-${i}`}
                              >
                                {variant.clicks ?? 0}
                              </span>{' '}
                              clicks
                            </span>
                            <span>
                              <span className="font-mono font-semibold text-foreground">{variant.weight}%</span>{' '}
                              traffic
                            </span>
                            {variant.confidence != null && (
                              <span data-testid={`ab-confidence-${i}`}>
                                <span className="font-mono font-semibold text-foreground">
                                  {variant.confidence}%
                                </span>{' '}
                                confidence
                              </span>
                            )}
                            {variant.lift != null && (
                              <span data-testid={`ab-lift-${i}`}>
                                <span
                                  className={`font-mono font-semibold ${
                                    variant.lift >= 0 ? 'text-emerald-600' : 'text-rose-500'
                                  }`}
                                >
                                  {variant.lift >= 0 ? '+' : ''}
                                  {variant.lift}%
                                </span>{' '}
                                lift
                              </span>
                            )}
                          </div>
                        </div>
                        {!abStats.winner_declared && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAbDeclareWinner(i)}
                            className="rounded-xl border-border text-xs gap-1 shrink-0"
                            data-testid={`ab-declare-winner-${i}`}
                          >
                            <Trophy size={12} weight="bold" /> Declare winner
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {abStats.winner_declared && (
                  <div
                    className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4 text-sm text-emerald-700 flex items-center gap-2"
                    data-testid="ab-winner-declared-notice"
                  >
                    <Trophy size={14} weight="fill" /> Winner declared — link destination has been updated.
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        {/* UTM */}
        <TabsContent value="utm" className="space-y-5" data-testid="utm-tab-content">
          <header className="lfld-tab-section-head">
            <div>
              <h2 className="lfld-tab-section-title">
                UTM <em>builder</em>.
              </h2>
              <p className="lfld-tab-section-sub">Attach campaign parameters so every click is attributed.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0 justify-end">
              <select
                key={utmTemplateTick}
                aria-label="Load UTM template"
                defaultValue=""
                disabled={!utmSavedTemplates.length}
                className="h-9 min-w-[148px] max-w-[220px] rounded-xl border border-border bg-white px-2.5 text-xs font-semibold text-foreground disabled:opacity-45"
                data-testid="utm-load-template-select"
                onChange={(e) => {
                  const v = e.target.value;
                  if (v) applyUtmTemplate(v);
                }}
              >
                <option value="">Load template…</option>
                {utmSavedTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 rounded-xl border-border text-xs font-semibold"
                onClick={() => {
                  setUtmTemplateName('');
                  setUtmSaveTemplateOpen(true);
                }}
                data-testid="utm-save-template-btn"
              >
                Save as template
              </Button>
            </div>
          </header>

          <div className="surface-card lfld-mock-panel">
            <div className="lfld-panel-head">
              <div>
                <div className="lfld-panel-title">Live preview</div>
                <div className="lfld-panel-sub">Updates as you type · click to copy</div>
              </div>
            </div>
            <div className="lfld-utm-form">
              {(() => {
                const pairs = utmPairsFromBuilder(utmBuilder);
                const qs = buildUtmQuery(utmBuilder);
                const full = qs ? `${shortUrl}?${qs}` : shortUrl;
                return (
                  <button
                    type="button"
                    className="lfld-utm-preview w-full text-left"
                    onClick={() => {
                      navigator.clipboard?.writeText(full).then(
                        () => toast.success('Copied full URL'),
                        () => toast.error('Could not copy')
                      );
                    }}
                  >
                    <span className="utm-base">{shortUrl}</span>
                    {pairs.map(([k, v], idx) => (
                      <span key={k}>
                        {idx === 0 ? <span className="utm-eq">?</span> : <span className="utm-eq">&</span>}
                        <span className="utm-key">{k}</span>
                        <span className="utm-eq">=</span>
                        <span className="utm-val">{v}</span>
                      </span>
                    ))}
                  </button>
                );
              })()}

              <div className="lfld-utm-grid">
                <div className="lfld-utm-field">
                  <label htmlFor="utm-source">source</label>
                  <input
                    id="utm-source"
                    value={utmBuilder.source}
                    onChange={(e) => setUtmBuilder({ ...utmBuilder, source: e.target.value })}
                    placeholder="e.g. linkedin, twitter, newsletter"
                  />
                </div>
                <div className="lfld-utm-field">
                  <label htmlFor="utm-medium">medium</label>
                  <input
                    id="utm-medium"
                    value={utmBuilder.medium}
                    onChange={(e) => setUtmBuilder({ ...utmBuilder, medium: e.target.value })}
                    placeholder="e.g. organic, paid, email, cpc"
                  />
                </div>
                <div className="lfld-utm-field">
                  <label htmlFor="utm-campaign">campaign</label>
                  <input
                    id="utm-campaign"
                    value={utmBuilder.campaign}
                    onChange={(e) => setUtmBuilder({ ...utmBuilder, campaign: e.target.value })}
                    placeholder="e.g. spring-launch-2026"
                  />
                </div>
                <div className="lfld-utm-field">
                  <label htmlFor="utm-term">term</label>
                  <input
                    id="utm-term"
                    value={utmBuilder.term}
                    onChange={(e) => setUtmBuilder({ ...utmBuilder, term: e.target.value })}
                    placeholder="paid keyword (optional)"
                  />
                </div>
                <div className="lfld-utm-field lfld-utm-field-full">
                  <label htmlFor="utm-content">content</label>
                  <input
                    id="utm-content"
                    value={utmBuilder.content}
                    onChange={(e) => setUtmBuilder({ ...utmBuilder, content: e.target.value })}
                    placeholder="e.g. cta-top, banner-a (optional)"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="surface-card lfld-mock-panel p-6">
            <div className="lfld-panel-head" style={{ marginBottom: 14 }}>
              <div>
                <div className="lfld-panel-title">UTM campaign breakdown</div>
                <div className="lfld-panel-sub">Clicks attributed to utm_campaign in this window</div>
              </div>
            </div>
            {analytics?.utm_campaigns?.length > 0 ? (
              <div style={{ height: Math.max(220, analytics.utm_campaigns.length * 36) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={analytics.utm_campaigns}
                    layout="vertical"
                    margin={{ left: 8, right: 16, top: 4, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 6" stroke="rgba(15,16,32,0.06)" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#6B6F86' }} tickLine={false} axisLine={false} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={150}
                      tick={{ fontSize: 11, fill: '#0F1020' }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip {...TooltipChrome} />
                    <Bar dataKey="value" fill="#4648d4" radius={[0, 8, 8, 0]} name="Clicks" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No UTM data in this period</p>
            )}
          </div>
        </TabsContent>

        {/* Routing */}
        <TabsContent value="routing" data-testid="routing-tab-content">
          <div className="space-y-5">
            <div className="surface-card p-6">
              <div className="flex items-start gap-3 mb-1">
                <span className="grid place-items-center h-10 w-10 shrink-0 rounded-xl bg-primary/10 text-primary">
                  <Globe size={20} weight="bold" />
                </span>
                <div>
                  <h3 className="font-display text-base font-bold text-foreground">Default route</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    When no rule matches — UTM presets on the link still merge into the redirect.
                  </p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2 sm:gap-3">
                <code className="text-xs sm:text-sm font-semibold rounded-xl bg-primary text-primary-foreground px-3 py-2 truncate max-w-full">
                  {shortenUrlDisplay(shortUrl.replace(/^https:\/\//, ''), 48)}
                </code>
                <span className="text-muted-foreground text-sm shrink-0">→</span>
                <div className="rounded-xl border border-border bg-muted/40 px-3 py-2 text-sm font-medium min-w-0 flex-1 sm:flex-none">
                  <span className="text-muted-foreground text-[10px] block uppercase tracking-wide">Destination</span>
                  <span className="truncate block max-w-[min(340px,100%)]">{link.destination_url || '—'}</span>
                  <span className="text-xs text-muted-foreground">{destinationDisplayHost(link.destination_url)}</span>
                </div>
              </div>
            </div>

            <div className="surface-card p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-display text-base font-bold text-foreground inline-flex items-center gap-2">
                  <Signpost size={16} weight="fill" className="text-primary" />
                  Routing rules
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Evaluated top to bottom — first match wins.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddRoutingRule}
                className="rounded-xl border-border text-xs gap-1.5"
                data-testid="routing-add-rule-btn"
              >
                <Plus size={12} weight="bold" /> Add rule
              </Button>
            </div>

            {routingRules.length === 0 ? (
              <div
                className="text-center py-12 rounded-2xl border border-dashed border-border bg-muted/30 text-sm text-muted-foreground"
                data-testid="routing-empty-state"
              >
                <ShieldCheck size={28} weight="duotone" className="mx-auto mb-2 text-muted-foreground" />
                No routing rules configured. Click "Add rule" to get started.
              </div>
            ) : (
              <div className="space-y-3">
                {routingRules.map((rule, i) => (
                  <div
                    key={i}
                    className="rounded-2xl border border-border bg-muted/30 p-5"
                    data-testid={`routing-rule-${i}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="chip bg-primary/10 text-primary">Rule {i + 1}</span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleMoveRoutingRule(i, -1)}
                          disabled={i === 0}
                          className="grid place-items-center h-7 w-7 rounded-lg border border-border text-muted-foreground hover:text-primary hover:border-primary/40 disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`routing-rule-up-${i}`}
                          title="Move up"
                        >
                          <ArrowUp size={12} weight="bold" />
                        </button>
                        <button
                          onClick={() => handleMoveRoutingRule(i, 1)}
                          disabled={i === routingRules.length - 1}
                          className="grid place-items-center h-7 w-7 rounded-lg border border-border text-muted-foreground hover:text-primary hover:border-primary/40 disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`routing-rule-down-${i}`}
                          title="Move down"
                        >
                          <ArrowDown size={12} weight="bold" />
                        </button>
                        <button
                          onClick={() => handleRemoveRoutingRule(i)}
                          className="grid place-items-center h-7 w-7 rounded-lg border border-rose-100 text-rose-500 hover:bg-rose-50"
                          data-testid={`routing-rule-remove-${i}`}
                          title="Remove rule"
                        >
                          <X size={12} weight="bold" />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-1.5 block">
                          Condition
                        </label>
                        <Select
                          value={rule.condition_type}
                          onValueChange={(v) => handleRoutingConditionTypeChange(i, v)}
                        >
                          <SelectTrigger
                            className="rounded-xl bg-white border-border h-10 text-sm"
                            data-testid={`routing-rule-type-${i}`}
                          >
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="geo_country">Country (Geo)</SelectItem>
                            <SelectItem value="device_type">Device type</SelectItem>
                            <SelectItem value="device_and_os">Device + OS</SelectItem>
                            <SelectItem value="referrer_contains">Referrer contains</SelectItem>
                            <SelectItem value="schedule">Schedule (UTC)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-1.5 block">
                          Value
                        </label>
                        {rule.condition_type === 'device_type' ? (
                          <Select
                            value={rule.condition_value}
                            onValueChange={(v) => handleRoutingRuleChange(i, 'condition_value', v)}
                          >
                            <SelectTrigger
                              className="rounded-xl bg-white border-border h-10 text-sm"
                              data-testid={`routing-rule-value-${i}`}
                            >
                              <SelectValue placeholder="Select device" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="mobile">Mobile</SelectItem>
                              <SelectItem value="tablet">Tablet</SelectItem>
                              <SelectItem value="desktop">Desktop</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : rule.condition_type === 'device_and_os' ? (
                          <div className="grid grid-cols-2 gap-2">
                            {(() => {
                              const duo = parseRoutingDeviceOs(rule.condition_value);
                              const setDuo = (next) =>
                                handleRoutingRuleChange(i, 'condition_value', JSON.stringify(next));
                              return (
                                <>
                                  <Select
                                    value={duo.device}
                                    onValueChange={(v) => setDuo({ ...duo, device: v })}
                                  >
                                    <SelectTrigger className="rounded-xl bg-white border-border h-10 text-sm">
                                      <SelectValue placeholder="Device" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="mobile">Mobile</SelectItem>
                                      <SelectItem value="tablet">Tablet</SelectItem>
                                      <SelectItem value="desktop">Desktop</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <Select
                                    value={duo.os}
                                    onValueChange={(v) => setDuo({ ...duo, os: v })}
                                  >
                                    <SelectTrigger className="rounded-xl bg-white border-border h-10 text-sm">
                                      <SelectValue placeholder="OS" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {ROUTING_OS_OPTIONS.map((o) => (
                                        <SelectItem key={o} value={o}>
                                          {o}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </>
                              );
                            })()}
                          </div>
                        ) : (
                          <div className="space-y-1.5">
                            <Input
                              value={rule.condition_value}
                              onChange={(e) => handleRoutingRuleChange(i, 'condition_value', e.target.value)}
                              placeholder={
                                rule.condition_type === 'geo_country'
                                  ? 'United States · or ISO (US, NL) on CF edge'
                                  : rule.condition_type === 'schedule'
                                  ? 'e.g. 09:00-17:00'
                                  : rule.condition_type === 'referrer_contains'
                                  ? 'e.g. linkedin.com'
                                  : 'Enter value'
                              }
                              className="rounded-xl border-border bg-white h-10 text-sm"
                              data-testid={`routing-rule-value-${i}`}
                            />
                            {rule.condition_type === 'geo_country' && (
                              <p className="text-[10px] text-muted-foreground leading-snug">
                                Cloudflare uses ISO country codes (e.g. <code className="text-foreground">US</code>).
                                Legacy paths may use GeoIP names — match whatever your traffic uses.
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-1.5 block">
                          Destination URL
                        </label>
                        <Input
                          value={rule.destination_url}
                          onChange={(e) => handleRoutingRuleChange(i, 'destination_url', e.target.value)}
                          placeholder="https://example.com/page"
                          className="rounded-xl border-border bg-white h-10 text-sm"
                          data-testid={`routing-rule-destination-${i}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {routingRules.length > 0 && (
              <div className="flex justify-end mt-5">
                <Button
                  onClick={handleSaveRoutingRules}
                  disabled={routingSaving}
                  className="rounded-xl gradient-primary text-white shadow-glow h-10 text-sm font-semibold px-5"
                  data-testid="routing-save-btn"
                >
                  {routingSaving ? 'Saving…' : 'Save rules'}
                </Button>
              </div>
            )}
          </div>
          </div>
        </TabsContent>
        <TabsContent value="alerts">
          <div className="surface-card p-6">
            <AlertRulesPanel linkId={linkId} />
          </div>
        </TabsContent>

        {/* Health Tab */}
        <TabsContent value="health">
          <div className="surface-card p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-display text-base font-bold text-foreground flex items-center gap-2">
                  <Heart size={17} weight="duotone" className="text-primary" />
                  Link health
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Automatic checks every 6 hours. We monitor your destination URL for errors, timeouts, and redirects.
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCheckNow}
                disabled={healthChecking}
                className="rounded-xl h-9 px-4 gap-1.5 font-semibold border-border"
              >
                {healthChecking
                  ? <CircleNotch size={13} className="animate-spin" />
                  : <ArrowClockwise size={13} weight="bold" />}
                {healthChecking ? 'Checking…' : 'Check now'}
              </Button>
            </div>

            <HealthBadgeLarge
              health={healthData?.status || link?.health_status}
              httpStatus={healthData?.http_status || link?.health_status_code}
              checkedAt={healthData?.checked_at || link?.health_checked_at}
            />

            {(healthData || (healthHistory && healthHistory.length > 0)) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="health-snapshot-cards">
                <div className="rounded-xl border border-border bg-muted/20 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    HTTP status
                  </p>
                  <p className="mt-1 text-xl font-bold text-foreground font-mono tracking-tight">
                    {healthData?.http_status ?? link?.health_status_code ?? '—'}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-2">Latest successful probe status code</p>
                </div>
                <div className="rounded-xl border border-border bg-muted/20 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Avg latency
                  </p>
                  <p className="mt-1 text-xl font-bold text-foreground">
                    {avgHealthLatencyMs != null ? `${avgHealthLatencyMs}ms` : '—'}
                  </p>
                  {healthLatencySparkline && (
                    <svg className="mt-2 h-8 w-full" viewBox="0 0 100 28" preserveAspectRatio="none" aria-hidden>
                      <polyline
                        fill="none"
                        stroke="#4648d4"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        points={healthLatencySparkline}
                      />
                    </svg>
                  )}
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Rolling average across loaded checks
                  </p>
                </div>
                <div className="rounded-xl border border-border bg-muted/20 p-4 min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Destination host
                  </p>
                  <p className="mt-1 text-sm font-semibold text-foreground truncate">{destinationDisplayHost(link?.destination_url)}</p>
                </div>
                <div className="rounded-xl border border-border bg-muted/20 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Checks in view
                  </p>
                  <p className="mt-1 text-xl font-bold text-foreground">{(healthHistory || []).length}</p>
                  <p className="text-[10px] text-muted-foreground mt-2">
                    Scheduled checks run about every <span className="font-semibold text-foreground">6 hours</span> per link.
                  </p>
                </div>
              </div>
            )}

            {/* Health history */}
            {healthHistory.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-3">
                  Recent checks
                </h4>
                <div className="rounded-xl border border-border overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50">
                      <tr className="border-b border-border">
                        <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground">Status</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground">HTTP</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground">Response time</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground">Checked at</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/50">
                      {healthHistory.slice(0, 20).map((h, i) => (
                        <tr key={i} className="hover:bg-muted/20">
                          <td className="px-4 py-2.5">
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border ${
                              h.status === 'healthy' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              h.status === 'broken'  ? 'bg-rose-50 text-rose-700 border-rose-200' :
                              h.status === 'timeout' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                              'bg-blue-50 text-blue-700 border-blue-200'
                            }`}>
                              {h.status}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 font-mono text-muted-foreground">{h.http_status || '—'}</td>
                          <td className="px-4 py-2.5 text-muted-foreground">{h.response_time_ms ? `${h.response_time_ms}ms` : '—'}</td>
                          <td className="px-4 py-2.5 text-muted-foreground">{new Date(h.checked_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {healthHistory.length === 0 && !healthData && (
              <div className="rounded-xl border border-dashed border-border/80 bg-muted/10 py-10 text-center">
                <Heart size={32} className="mx-auto mb-2 text-muted-foreground opacity-30" />
                <p className="text-sm font-semibold text-muted-foreground">No health data yet</p>
                <p className="text-xs text-muted-foreground mt-1">Click "Check now" to run the first health check.</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="sm:max-w-2xl rounded-2xl border border-border max-h-[90vh] overflow-y-auto p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center h-10 w-10 rounded-xl gradient-primary text-white shadow-glow">
                <PencilSimple size={16} weight="fill" />
              </span>
              <div>
                <DialogTitle className="font-display text-xl font-extrabold text-foreground">
                  Edit link
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5">/{link.slug} — destination &amp; settings</p>
              </div>
            </div>
          </DialogHeader>
          <div className="px-6 py-5 space-y-4">
            <FieldRow label="Slug (immutable)">
              <input
                disabled
                value={`/${link.slug}`}
                className="input-modern bg-muted/60 text-muted-foreground cursor-not-allowed"
              />
            </FieldRow>

            <FieldRow label="Destination URL">
              <input
                value={editForm.destination_url}
                onChange={(e) => setEditForm({ ...editForm, destination_url: e.target.value })}
                className="input-modern"
                data-testid="edit-destination-input"
              />
            </FieldRow>

            <div className="grid sm:grid-cols-2 gap-4">
              <FieldRow label="Title">
                <input
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="input-modern"
                  data-testid="edit-title-input"
                />
              </FieldRow>
              <FieldRow label="Status">
                <Select
                  value={editForm.status}
                  onValueChange={(v) => setEditForm({ ...editForm, status: v })}
                >
                  <SelectTrigger
                    className="h-10 rounded-xl bg-white border-border"
                    data-testid="edit-status-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </FieldRow>
            </div>

            <FieldRow label="Tags" hint="comma separated">
              <input
                value={editForm.tags}
                onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                className="input-modern"
                data-testid="edit-tags-input"
              />
            </FieldRow>

            <FieldRow label="Folder" hint="optional">
              <Select
                value={editForm.folder_id || 'none'}
                onValueChange={(v) => setEditForm({ ...editForm, folder_id: v })}
              >
                <SelectTrigger
                  className="h-10 rounded-xl bg-white border-border"
                  data-testid="edit-folder-select"
                >
                  <SelectValue placeholder="No folder" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No folder</SelectItem>
                  {availableFolders.map((f) => (
                    <SelectItem key={f.folder_id} value={f.folder_id}>
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>

            <div className="grid sm:grid-cols-2 gap-4">
              <FieldRow label="Expiry date">
                <input
                  type="datetime-local"
                  value={editForm.expiry_date}
                  onChange={(e) => setEditForm({ ...editForm, expiry_date: e.target.value })}
                  className="input-modern"
                  data-testid="edit-expiry-input"
                />
              </FieldRow>
              <FieldRow label="Max clicks">
                <input
                  type="number"
                  value={editForm.max_clicks}
                  onChange={(e) => setEditForm({ ...editForm, max_clicks: e.target.value })}
                  placeholder="e.g. 1000"
                  className="input-modern"
                  data-testid="edit-max-clicks-input"
                />
              </FieldRow>
            </div>

            <FieldRow label="Password protection">
              <input
                type="text"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                placeholder="Leave empty to remove password"
                className="input-modern"
                data-testid="edit-password-input"
              />
            </FieldRow>

            <FieldRow label="UTM parameters">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[
                  ['utm_source', 'source', 'edit-utm-source'],
                  ['utm_medium', 'medium', 'edit-utm-medium'],
                  ['utm_campaign', 'campaign', 'edit-utm-campaign'],
                  ['utm_term', 'term', 'edit-utm-term'],
                  ['utm_content', 'content', 'edit-utm-content'],
                ].map(([key, placeholder, testId]) => (
                  <input
                    key={key}
                    value={editForm[key] || ''}
                    onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                    placeholder={placeholder}
                    className="input-modern h-9 text-xs"
                    data-testid={testId}
                  />
                ))}
              </div>
            </FieldRow>

            <FieldRow label="CTA overlay">
              <Select
                value={editForm.cta_overlay_id || 'none'}
                onValueChange={(v) =>
                  setEditForm({ ...editForm, cta_overlay_id: v === 'none' ? '' : v })
                }
              >
                <SelectTrigger
                  className="h-10 rounded-xl bg-white border-border"
                  data-testid="edit-cta-select"
                >
                  <SelectValue placeholder="None (no CTA)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {availableCtas.map((cta) => (
                    <SelectItem key={cta.cta_id} value={cta.cta_id}>
                      {cta.name} — {cta.headline}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>

            {availablePixels.length > 0 && (
              <FieldRow label="Retargeting pixels">
                <div className="grid sm:grid-cols-2 gap-2" data-testid="edit-pixels-list">
                  {availablePixels.map((pixel) => {
                    const checked = editPixels.includes(pixel.pixel_id);
                    return (
                      <label
                        key={pixel.pixel_id}
                        className={`flex items-center gap-2.5 cursor-pointer rounded-xl border p-3 transition-all ${
                          checked ? 'border-primary bg-primary/5' : 'border-border bg-white hover:border-primary/40'
                        }`}
                        data-testid={`edit-pixel-option-${pixel.pixel_id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            setEditPixels((prev) =>
                              checked
                                ? prev.filter((id) => id !== pixel.pixel_id)
                                : [...prev, pixel.pixel_id]
                            )
                          }
                          className="h-4 w-4 accent-primary cursor-pointer shrink-0"
                          data-testid={`edit-pixel-checkbox-${pixel.pixel_id}`}
                        />
                        <span className="text-sm font-medium text-foreground truncate">{pixel.name}</span>
                        {pixel.pixel_code && (
                          <span className="text-[10px] text-muted-foreground font-mono truncate ml-auto">
                            {pixel.pixel_code}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </FieldRow>
            )}
          </div>
          <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-2.5 bg-white rounded-b-2xl">
            <Button variant="outline" onClick={() => setShowEdit(false)} className="rounded-xl border-border h-10">
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              className="rounded-xl gradient-primary text-white shadow-glow h-10 px-5 font-semibold"
              data-testid="save-edit-btn"
            >
              Save changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Dialog */}
      <Dialog open={showQR} onOpenChange={setShowQR}>
        <DialogContent className="sm:max-w-md rounded-2xl border border-border p-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-border">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center h-10 w-10 rounded-xl gradient-primary text-white shadow-glow">
                <QrCode size={16} weight="fill" />
              </span>
              <div>
                <DialogTitle className="font-display text-xl font-extrabold text-foreground">
                  QR code
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5">Customise colours, then download as SVG.</p>
              </div>
            </div>
          </DialogHeader>
          <div className="px-6 py-5 flex flex-col items-center gap-5">
            <div
              id="qr-code-svg"
              className="p-5 bg-white border border-border rounded-2xl shadow-soft"
              data-testid="qr-code-display"
            >
              <QRCodeSVG value={`${shortUrl}?qr=1`} size={200} fgColor={qrFg} bgColor={qrBg} level="H" />
            </div>
            <div className="grid grid-cols-2 gap-4 w-full">
              <FieldRow label="Foreground">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={qrFg}
                    onChange={(e) => setQrFg(e.target.value)}
                    className="h-10 w-10 rounded-xl border border-border cursor-pointer"
                    data-testid="qr-fg-color"
                  />
                  <span className="text-xs font-mono text-muted-foreground">{qrFg}</span>
                </div>
              </FieldRow>
              <FieldRow label="Background">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={qrBg}
                    onChange={(e) => setQrBg(e.target.value)}
                    className="h-10 w-10 rounded-xl border border-border cursor-pointer"
                    data-testid="qr-bg-color"
                  />
                  <span className="text-xs font-mono text-muted-foreground">{qrBg}</span>
                </div>
              </FieldRow>
            </div>
            <Button
              onClick={handleDownloadQR}
              className="w-full rounded-xl gradient-primary text-white shadow-glow h-11 gap-2 font-semibold"
              data-testid="download-qr-btn"
            >
              <DownloadSimple size={16} weight="bold" /> Download SVG
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={utmSaveTemplateOpen} onOpenChange={setUtmSaveTemplateOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl border border-border p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-border">
            <DialogTitle className="font-display text-lg font-bold">Save UTM template</DialogTitle>
            <p className="text-xs text-muted-foreground pt-1">
              Templates are saved in this browser for workspace <strong>{workspace?.name || 'current'}</strong>.
            </p>
          </DialogHeader>
          <div className="px-6 py-4 space-y-3">
            <FieldRow label="Template name">
              <Input
                value={utmTemplateName}
                onChange={(e) => setUtmTemplateName(e.target.value)}
                placeholder="e.g. LinkedIn paid — spring promo"
                className="rounded-xl border-border bg-white"
                onKeyDown={(e) => e.key === 'Enter' && handleSaveUtmTemplate()}
                data-testid="utm-template-name-input"
              />
            </FieldRow>
          </div>
          <div className="px-6 py-4 border-t border-border flex justify-end gap-2 bg-muted/20 rounded-b-2xl">
            <Button
              type="button"
              variant="outline"
              className="rounded-xl border-border"
              onClick={() => setUtmSaveTemplateOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="rounded-xl gradient-primary text-white shadow-glow font-semibold"
              onClick={handleSaveUtmTemplate}
              data-testid="utm-template-save-confirm"
            >
              Save template
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FieldRow({ label, hint, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {label}
        </span>
        {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
